/**
 * Scroll-triggered sound setup
 * Plays a scene custom sound once when a scroll trigger point is reached.
 */

import { getAudioManager } from '../../../features/audio'

export interface ScrollSoundHandler {
  cleanup: () => void
}

export interface AudioTriggerConfig {
  start: { element: number; viewport: number }
}

// ---------------------------------------------------------------------------
// Global suppression flag – silences scroll sounds during programmatic navigation
// ---------------------------------------------------------------------------

let suppressed = false

/** Suppress all scroll-triggered sounds (call before programmatic scroll). */
export function suppressScrollSounds(): void {
  suppressed = true
}

/** Re-enable scroll-triggered sounds (call after programmatic scroll finishes). */
export function resumeScrollSounds(): void {
  suppressed = false
}

/** Check whether scroll sounds are currently suppressed. */
export function isScrollSoundsSuppressed(): boolean {
  return suppressed
}

/**
 * Setup a scroll-triggered sound from the audio.sounds[] config.
 * Plays once when the trigger point is crossed while scrolling down.
 * When trigger config is provided, uses element/viewport percentages.
 * Falls back to center/center with 50px tolerance for legacy callers.
 */
export function setupScrollIntoCenterSound(
  element: HTMLElement,
  sceneId: string,
  fileName: string,
  options?: {
    optional?: boolean
    trigger?: AudioTriggerConfig
    volumeCategory?: 'speech' | 'sound'
  }
): ScrollSoundHandler {
  const lenis = (window as any).__lenis as any
  if (!lenis) {
    return { cleanup: () => {} }
  }

  let lastScrollY = lenis.scroll || window.scrollY
  let wasInZone = false
  let hasPlayed = false
  const TOLERANCE = 50

  const trigger = options?.trigger ?? { start: { element: 50, viewport: 50 } }

  const scrollHandler = () => {
    const currentScrollY = lenis.scroll || window.scrollY
    const isScrollingDown = currentScrollY > lastScrollY
    lastScrollY = currentScrollY

    if (suppressed) {
      wasInZone = false
      return
    }

    const rect = element.getBoundingClientRect()
    const vh = window.innerHeight
    const elementPoint = rect.top + rect.height * (trigger.start.element / 100)
    const viewportPoint = vh * (trigger.start.viewport / 100)
    const isInZone = Math.abs(elementPoint - viewportPoint) < TOLERANCE

    if (!isScrollingDown || !isInZone) {
      if (!isInZone) hasPlayed = false
      wasInZone = isInZone
      return
    }

    if (isScrollingDown && isInZone && !wasInZone && !hasPlayed) {
      const audioManager = getAudioManager()
      const cat = options?.volumeCategory ?? 'sound'
      if (options?.optional) {
        audioManager.tryPlayCustomSound(fileName, sceneId, cat)
      } else {
        audioManager.playCustomSound(fileName, sceneId, cat)
      }
      hasPlayed = true
    }

    wasInZone = isInZone
  }

  lenis.on('scroll', scrollHandler)
  return { cleanup: () => { lenis.off('scroll', scrollHandler) } }
}
